import { useState, useEffect } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Loading, Tag, StatusBadge } from "@/components/common";
import { Cpu, Sparkle, Spinner, ArrowsClockwise, CurrencyDollar, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const CAT_COLORS = { Creative: "#00E5FF", Game: "#FF007F", Business: "#FFB800", Technical: "#00FF9D" };
const PROVIDER_LABELS = { anthropic: "Anthropic", openai: "OpenAI", gemini: "Gemini", local: "Self-hosted (Local)" };

export default function AIAgents() {
  const [cat, setCat] = useState(null);
  const [usage, setUsage] = useState(null);
  const [assign, setAssign] = useState({});
  const [saving, setSaving] = useState(false);
  const [testAgent, setTestAgent] = useState("creative_director");
  const [testText, setTestText] = useState("Give me one fresh episode idea for our audience.");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [budget, setBudget] = useState(null);

  const load = () => {
    api.get("/ai/catalog").then(({ data }) => {
      setCat(data);
      const m = {}; data.agents.forEach((a) => { m[a.id] = a.resolved_model; }); setAssign(m);
    });
    api.get("/ai/usage").then(({ data }) => setUsage(data)).catch(() => setUsage(null));
    api.get("/ai/budget").then(({ data }) => setBudget(data)).catch(() => setBudget(null));
  };
  useEffect(() => { load(); }, []);

  const saveBudget = async (patch) => {
    try { const { data } = await api.put("/ai/budget", patch); setBudget(data); toast.success(patch.emergency_stop !== undefined ? (patch.emergency_stop ? "Emergency stop ON — AI paused" : "Emergency stop OFF") : "Budget updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Owner/Admin only"); }
  };

  const saveAssign = async () => {
    setSaving(true);
    try { const { data } = await api.put("/ai/agents", { agent_models: assign }); setCat(data); toast.success("Agent model assignments saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Only owner/AI-provider managers can change this"); }
    finally { setSaving(false); }
  };
  const toggleProvider = async (p) => {
    const enabled = cat.enabled_providers.includes(p) ? cat.enabled_providers.filter((x) => x !== p) : [...cat.enabled_providers, p];
    try { const { data } = await api.put("/ai/providers-config", { enabled_providers: enabled }); setCat(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Not allowed"); }
  };
  const runTest = async () => {
    if (!testText.trim()) return;
    setRunning(true); setResult(null);
    try { const { data } = await api.post(`/ai/agent/${testAgent}/run`, { text: testText }); setResult(data); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Agent run failed"); }
    finally { setRunning(false); }
  };

  if (!cat) return <Loading />;
  const categories = [...new Set(cat.agents.map((a) => a.category))];
  const textModels = cat.models.filter((m) => m.provider !== "local");

  return (
    <div>
      <PageHeader icon={Cpu} title="AI Agents & Router"
        subtitle="Assign any model to any specialized agent. Requests fall back across providers on failure, and every call is cost-logged. You are never locked to one AI company."
        badge={<StatusBadge status="LIVE" />} />

      <Panel className="mb-6">
        <div className="flex items-center gap-2 mb-3"><Lightning size={18} weight="fill" className="text-[#FFB800]" /><span className="font-display font-bold text-white">Providers</span><span className="text-xs text-zinc-500">Enable/disable providers used by the router (server-side keys)</span></div>
        <div className="flex flex-wrap gap-2">
          {cat.providers.map((p) => (
            <button key={p} data-testid={`provider-${p}`} onClick={() => toggleProvider(p)}
              className={`rounded-full px-3 py-1.5 text-sm border ${cat.enabled_providers.includes(p) ? "border-[#00FF9D]/50 bg-[#00FF9D]/10 text-[#00FF9D]" : "border-white/10 text-zinc-500"}`}>
              {PROVIDER_LABELS[p] || p}{p === "local" ? " · setup required" : ""}
            </button>
          ))}
        </div>
      </Panel>

      {budget && (
        <Panel className="mb-6">
          <div className="flex items-center gap-2 mb-3"><CurrencyDollar size={18} weight="fill" className="text-[#FF3366]" /><span className="font-display font-bold text-white">Budget Guardrails</span>{budget.blocked && <StatusBadge status="REVIEW" />}</div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div><div className="text-xs text-zinc-500 mb-1">Daily limit (£, 0 = off)</div>
              <input data-testid="budget-daily" type="number" min="0" defaultValue={budget.daily_limit} onBlur={(e) => saveBudget({ daily_limit: Number(e.target.value) })} className="w-36 rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-white outline-none" /></div>
            <div><div className="text-xs text-zinc-500 mb-1">Monthly limit (£, 0 = off)</div>
              <input data-testid="budget-monthly" type="number" min="0" defaultValue={budget.monthly_limit} onBlur={(e) => saveBudget({ monthly_limit: Number(e.target.value) })} className="w-36 rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-white outline-none" /></div>
            <div className="text-xs text-zinc-400">Spent today <span className="text-white font-semibold">£{Number(budget.spent_today).toFixed(2)}</span> · this month <span className="text-white font-semibold">£{Number(budget.spent_month).toFixed(2)}</span></div>
            <button data-testid="emergency-stop" onClick={() => saveBudget({ emergency_stop: !budget.emergency_stop })}
              className={`sm:ml-auto rounded-full px-4 py-2 text-sm font-semibold border ${budget.emergency_stop ? "bg-[#FF3366] text-white border-[#FF3366]" : "border-[#FF3366]/40 text-[#FF3366] hover:bg-[#FF3366]/10"}`}>
              {budget.emergency_stop ? "Emergency Stop: ON (resume)" : "Emergency Stop"}
            </button>
          </div>
        </Panel>
      )}

      {usage && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Est. AI cost" value={`£${usage.total_est_cost?.toFixed?.(2) ?? "0.00"}`} icon={CurrencyDollar} />
          <Stat label="AI calls" value={usage.count} icon={Sparkle} />
          {Object.entries(usage.by_provider || {}).slice(0, 2).map(([p, v]) => <Stat key={p} label={`${p}`} value={`£${Number(v).toFixed(2)}`} icon={Cpu} />)}
        </div>
      )}

      {categories.map((c) => (
        <div key={c} className="mb-6">
          <h3 className="font-display text-lg font-bold mb-3" style={{ color: CAT_COLORS[c] || "#fff" }}>{c} Agents</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {cat.agents.filter((a) => a.category === c).map((a) => (
              <Panel key={a.id} data-testid={`agent-${a.id}`} className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white">{a.name}</span>
                  <Tag className="text-[10px]">{a.id}</Tag>
                </div>
                <p className="text-xs text-zinc-500">{a.desc}</p>
                <select data-testid={`agent-model-${a.id}`} value={assign[a.id] || a.resolved_model}
                  onChange={(e) => setAssign((m) => ({ ...m, [a.id]: e.target.value }))}
                  className="mt-1 rounded-xl bg-black/30 border border-white/10 px-3 py-2 text-sm text-white [color-scheme:dark]">
                  {textModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </Panel>
            ))}
          </div>
        </div>
      ))}

      <button data-testid="save-agents" onClick={saveAssign} disabled={saving}
        className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black disabled:opacity-60 mb-8">
        {saving ? <Spinner size={18} className="animate-spin" /> : <ArrowsClockwise size={18} weight="bold" />} Save Assignments
      </button>

      <Panel>
        <div className="flex items-center gap-2 mb-3"><Sparkle size={18} weight="fill" className="text-[#00E5FF]" /><span className="font-display font-bold text-white">Test an Agent (real, with fallback)</span></div>
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <select data-testid="test-agent-select" value={testAgent} onChange={(e) => setTestAgent(e.target.value)} className="sm:w-56 rounded-xl bg-black/30 border border-white/10 px-3 py-2.5 text-sm text-white [color-scheme:dark]">
            {cat.agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <input data-testid="test-agent-input" value={testText} onChange={(e) => setTestText(e.target.value)} className="flex-1 rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-white outline-none" />
          <button data-testid="test-agent-run" onClick={runTest} disabled={running} className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black disabled:opacity-60">
            {running ? <Spinner size={18} className="animate-spin" /> : <Sparkle size={18} weight="fill" />} Run
          </button>
        </div>
        {result && (
          <div data-testid="test-agent-result" className="rounded-xl border border-white/10 bg-black/20 p-4">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Tag>{result.provider}</Tag><Tag>{result.model}</Tag>
              {result.fell_back && <span className="text-xs text-[#FFB800]">↩ fell back from {result.primary_model_id}</span>}
              <span className="text-xs text-zinc-500 ml-auto">{result.latency_ms}ms · ~£{result.est_cost}</span>
            </div>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{result.text}</p>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <Panel className="flex items-center gap-3 py-3">
      <Icon size={20} weight="duotone" className="text-[#00E5FF]" />
      <div><div className="font-display text-xl font-extrabold text-white leading-none">{value}</div><div className="text-[11px] text-zinc-500 capitalize mt-0.5">{label}</div></div>
    </Panel>
  );
}
